<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="nature-white-box">
          <div class="white-box-top"></div>
          <TopBar
            :show-currencies="false"
            :show-delete="false"
            :show-languages="false"
            :show-select="false"
            :show-add="false"
            :methods="methods"
          />
          <div v-if="generating" class="alert alert-info text-center py-2 mb-2">
            <div class="spinner-border spinner-border-sm me-2" role="status"></div>
            Generating schedule plan...
          </div>
          <div class="row overflow-hidden mt-4">
            <div class="timeline-container w-100 dragscroll fix-height">
              <div v-if="loading" class="p-5 text-center w-100">
                <div class="spinner-border text-primary mb-3" role="status"></div>
                <h5>Loading production data...</h5>
              </div>
              <div v-else-if="list.length === 0" class="p-5 text-center w-100">
                <h5>No production data found</h5>
                <p>Click "Generate Schedule" to start the solver.</p>
              </div>
              <div v-else class="timeline">
                <div class="timeline-head">
                  <div class="timeline-title"></div>
                  <div
                    class="timeline-month"
                    :style="'width:' + dayWith + 'px'"
                    :class="[yearChange(i) ? 'year' : '']"
                    v-for="(date, i) in dates"
                    :key="date.i"
                  >
                    {{ date.year }} {{ date.monthName }} {{ date.day }}
                  </div>
                </div>
                <div
                  class="timeline-item"
                  v-for="service in list"
                  :key="service.id"
                >
                  <div class="timeline-title d-grid">
                    <div class="timeline-name">{{ service.name }}</div>
                    <div class="row ms-3 small">
                      <div class="col-6">{{ service.type }}</div>
                      <div class="col-6 text-end pe-4">
                        {{ service.secondary_label }}
                      </div>
                    </div>
                  </div>
                  <div class="timeline-row">
                    <div
                      class="timeline-line"
                      v-for="line in service.times"
                      :key="line.id"
                      :style="
                        'left:' +
                        start(line.start_time) +
                        'px;width: ' +
                        width(line.start_time, line.end_time) +
                        'px'
                      "
                      :class="{ highlighted: isHighlighted(line) }"
                      :title="line.description+' - '+line.name + ' ' + (line?.product?.serial || '')"
                    >
                      <div class="timeline-start-date">
                        {{ line.name }}
                        {{ line?.product?.serial }}
                      </div>
                      <div
                        class="timeline-end-date"
                        v-if="+width(line.start_time, line.end_time) > 200"
                      >
                        {{ date(line.end_time) }}
                      </div>
                      <div
                        class="timeline-color"
                        :style="'background: #' + (line.color || '90daf0')"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { mapState } from "vuex";
import TopBar from "../../components/lists/TopBar";
import http from "../../modules/http";
import ws from "../../modules/ws";

export default {
  name: "ManufacturingGrant",
  components: { TopBar },
  data() {
    return {
      loading: true,
      generating: false,
      dayWith: 1200,
      viewType: 0,
      methods: [
        {
          title: "Generate Schedule",
          icon: "play-circle",
          always: true,
          method: () => {
            this.generating = true;
            http.fetch("/production/plan/generate").catch(() => {
              this.generating = false;
            });
          },
        },
        {
          title: "Product view",
          icon: "box",
          always: true,
          method: () => {
            this.viewType = 0;
          },
        },
        {
          title: "User view",
          icon: "partners",
          always: true,
          method: () => {
            this.viewType = 1;
          },
        },
        {
          title: "Machine view",
          icon: "scissor",
          always: true,
          method: () => {
            this.viewType = 2;
          },
        },
        {
          title: "Zoom in",
          icon: "plus-circle",
          always: true,
          method: () => {
            if (this.dayWith > 6000) {
              return;
            }
            this.dayWith += 200;
          },
        },
        {
          title: "Zoom out",
          icon: "minus-circle",
          always: true,
          method: () => {
            if (this.dayWith < 300) {
              return;
            }
            this.dayWith -= 200;
          },
        },
      ],
      services: [],
      users: [],
      machines: [],
    };
  },
  computed: {
    ...mapState({
      searchQuery: (state) => state.topBar?.q || "",
    }),
    list() {
      // Map view type to data source
      let sourceData = this.services;

      if (this.viewType === 1) {
        sourceData = this.users.length > 0 ? this.users : this.services;
      } else if (this.viewType === 2) {
        sourceData = this.machines.length > 0 ? this.machines : this.services;
      }

      // Mapping engine: transform raw services into view-specific rows
      if (this.services.length > 0) {
        if (this.viewType === 1) { // User view
          const userMap = {};
          this.services.forEach(s => {
            const worker = s.worker;
            if (!worker) return;
            if (!userMap[worker.id]) {
              userMap[worker.id] = { id: worker.id, name: worker.name, type: 'Worker', secondary_label: '', times: [] };
            }
            userMap[worker.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(userMap);
        } else if (this.viewType === 2) { // Machine view
          const machineMap = {};
          this.services.forEach(s => {
            const asset = s.asset;
            if (!asset) return;
            if (!machineMap[asset.id]) {
              machineMap[asset.id] = { id: asset.id, name: asset.name, type: 'Machine', secondary_label: asset.model, times: [] };
            }
            machineMap[asset.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(machineMap);
        } else if (this.viewType === 0 && !this.services[0].times) { // Product view grouping
          const productMap = {};
          this.services.forEach(s => {
            const variant = s.item_operation?.item?.productVariant;
            const product = variant?.product;
            if (!product) return;
            const productName = product.serial ||  product.product_translations?.[0]?.name  || 'Product';
            if (!productMap[variant.id]) {
              productMap[variant.id] = { id: variant.id, name: productName, type: 'Product', secondary_label: product.serial, times: [] };
            }
            productMap[variant.id].times.push(this.mapScheduleToTime(s));
          });
          return Object.values(productMap);
        }
      }

      return sourceData;
    },
    now() {
      // Reference point is 09:00 today (start of shift)
      return dayjs().startOf("day").add(9, "hour");
    },
    dates() {
      let dates = [];
      const start = dayjs().startOf("day");
      for (let i = 0; i < 60; i++) {
        const d = start.add(i, "day");
        dates.push({
          day: d.date(),
          year: d.year(),
          month: d.month(),
          monthName: d.format("MMMM"),
          i: i,
        });
      }
      return dates;
    },
  },
  mounted() {
    this.loadData();
    ws.init();
    ws.subscribe("modify", "productionSchedule", () => {
      this.loadData();
      this.generating = false;
    });
    const slider = document.querySelector(".dragscroll");
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    });
  },
  unmounted() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    loadData() {
      this.loading = true;
      http.fetch("/production/plan").then((data) => {
        if (!data) return;
        this.generating = data.generating;
        let schedules = data.schedule;

        // Ensure we handle both structured object and flat array
        if (Array.isArray(schedules)) {
          this.services = schedules;
          this.users = [];
          this.machines = [];
        } else {
          this.services = schedules.products || [];
          this.users = schedules.users || [];
          this.machines = schedules.machines || [];
        }
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },
    getLastDayOfMonth(year, month) {
      return new Date(year, month + 1, 0).getDate();
    },
    date(val) {
      dayjs.locale("en");
      dayjs.extend(localizedFormat);
      dayjs.extend(relativeTime);
      return dayjs().to(dayjs(val));
    },
    diff(d1, d2) {
      let time;
      time = (d2.diff(d1)) / 1000;
      return time;
    },
    width(start, end) {
      let d1 = dayjs(start);
      let d2 = dayjs(end);
      return Math.max(10, this.diff(d1, d2) * (this.dayWith / 86400));
    },
    start(start) {
      let d1 = this.now;
      let d2 = dayjs(start);
      const diff = this.diff(d1, d2);
      // If start is before 09:00 today, it's a negative offset, but we want to show it.
      // However, we should probably cap it to not go too far left if it's days ago.
      return diff * (this.dayWith / 86400);
    },
    isHighlighted(line) {
      if (!this.searchQuery) return false;
      const q = this.searchQuery.toLowerCase();
      const product = line?.product;
      const productName =
        product?.product_translations?.[0]?.name ||
        product?.productTranslations?.[0]?.name ||
        "";
      const customerName = line?.customer?.name || "";
      const opName = line.name || "";
      const serial = product?.serial || "";

      return (
        productName.toLowerCase().includes(q) ||
        customerName.toLowerCase().includes(q) ||
        opName.toLowerCase().includes(q) ||
        serial.toLowerCase().includes(q)
      );
    },
    yearChange(i) {
      if (i === 0) {
        return false;
      }
      return this.dates[i - 1].year !== this.dates[i].year;
    },
    mapScheduleToTime(s) {
      return {
        id: s.id,
        name: s.product_manufacturing_operation?.manufacturingOperation?.name || 'Operation',
        start_time: s.start_time,
        end_time: s.end_time,
        color: s.color || '90daf0',
        product: s.item_operation?.item?.productVariant?.product,
        customer: s.item_operation?.warehouseOperation?.crmItem,
        description:  s.product_manufacturing_operation?.description ?? "",
      };
    },
  },
};
</script>
<style>
.timeline-container {
  height: calc(100vh - 230px);
  overflow-x: scroll;
  margin-left: calc(var(--bs-gutter-x) / -2);
}
.timeline-container .timeline {
  width: max-content;
  position: relative;
  margin-left: calc(var(--bs-gutter-x) / 2);
}
.timeline-container .timeline .timeline-line {
  position: absolute;
  height: 52px;
  border-radius: 6px;
  box-sizing: border-box;
  padding: 15px 10px;
  background: #f7f8ff;
  margin: 2px 0 0;
  box-shadow: 0 0 2px 1px rgba(202, 202, 202, 0.6);
  transition: all 0.3s ease;
}
.timeline-container .timeline .timeline-line.highlighted {
  background: #fff9c4;
  box-shadow: 0 0 8px 2px rgba(255, 235, 59, 0.8);
  z-index: 5;
}
.timeline-container .timeline .timeline-line.highlighted .timeline-start-date {
  background: #fff9c4;
}
.timeline-row {
  position: relative;
  width: 100%;
  height: 56px;
}
.timeline-container .timeline .timeline-line .timeline-start-date {
  position: sticky;
  left: 432px;
  margin-right: 136px;
  color: var(--gray-dark);
  background: #f7f8ff;
  z-index: 1;
  font-weight: 500;
  float: left;
}
.timeline-container .timeline .timeline-line .timeline-end-date {
  position: sticky;
  right: 10px;
  color: var(--gray-dark);
  font-weight: 500;
  float: right;
}
.timeline-container .timeline .timeline-line .timeline-color {
  position: sticky;
  left: 424px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: -10px;
}
.timeline-container .timeline .timeline-head {
  display: flex;
  padding-bottom: 1em;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
}
.timeline-container .timeline .timeline-head .timeline-month {
  width: 240px;
  border-left: 1px solid var(--gray);
  padding-left: 10px;
}
.timeline-container .timeline .timeline-head .timeline-month.year:before {
  content: "";
  width: 1px;
  height: 100%;
  border-left: 4px solid #713ae8;
  position: absolute;
  margin-left: -13px;
  border-radius: 6px;
}
.timeline-container .timeline .timeline-item {
  display: flex;
  gap: 0;
  margin-bottom: 0.5em;
  padding-bottom: 4px;
}
.timeline-container .timeline .timeline-title {
  width: 420px;
  z-index: 10;
  position: sticky;
  left: 0;
  background: rgb(255 255 255 / 86%);
  padding-left: calc(var(--bs-gutter-x) / 2);
  margin-bottom: -4px;
}
.timeline-container .timeline .timeline-name {
  font-size: 1.2em;
  font-weight: 500;
  margin-left: 4px;
}
.dragscroll {
  cursor: all-scroll;
}
</style>
